import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Upload, message, Typography, Space, Spin, Empty, Divider, Alert, Select } from 'antd';
import { UploadOutlined, PrinterOutlined, FileExcelOutlined, InfoCircleOutlined, SearchOutlined, DownloadOutlined, FileZipOutlined } from '@ant-design/icons';
import { parseBlankTimesheetExcel, getBlankTimesheets, exportBlankTimesheetsPdf, exportBlankTimesheetsZip } from '../api';
import logo from '../logo.png';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;

const months = Array.from({ length: 12 }, (_, i) => ({ label: moment().month(i).format('MMMM'), value: i + 1 }));
const years = Array.from({ length: 5 }, (_, i) => ({ label: String(moment().year() - 2 + i), value: moment().year() - 2 + i }));

const BlankTimesheets = () => {
    const now = moment();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [zipping, setZipping] = useState(false);
    const [data, setData] = useState([]);
    const [isPrinting, setIsPrinting] = useState(false);
    
    const [selectedMonth, setSelectedMonth] = useState(now.month() + 1);
    const [selectedYear, setSelectedYear] = useState(now.year());

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await getBlankTimesheets({ month: selectedMonth, year: selectedYear });
            if (res.success) {
                setData(res.data);
            }
        } catch (error) {
            message.error('Failed to fetch deployment data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedMonth, selectedYear]);

    const handleUpload = async () => {
        if (!file) {
            message.error('Please select an Excel file first.');
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('month', selectedMonth);
            formData.append('year', selectedYear);
            const response = await parseBlankTimesheetExcel(formData);
            
            if (response.success) {
                setData(response.data);
                setFile(null);
                message.success(response.message || 'Data processed successfully!');
            } else {
                message.error(response.message || 'Failed to process Excel.');
            }
        } catch (error) {
            message.error('An error occurred while uploading.');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        setIsPrinting(true);
        setTimeout(() => {
            window.print();
            setIsPrinting(false);
        }, 500);
    };

    const handleBulkDownload = async () => {
        setDownloading(true);
        try {
            const blob = await exportBlankTimesheetsPdf({ month: selectedMonth, year: selectedYear });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const monthLabel = months.find(m => m.value === selectedMonth)?.label;
            link.download = `Blank_Timesheets_${monthLabel}_${selectedYear}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            message.success('Combined PDF generated successfully!');
        } catch (error) {
            message.error('Failed to generate PDF.');
        } finally {
            setDownloading(false);
        }
    };

    const handleZipDownload = async () => {
        setZipping(true);
        try {
            const blob = await exportBlankTimesheetsZip({ month: selectedMonth, year: selectedYear });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const monthLabel = months.find(m => m.value === selectedMonth)?.label;
            link.download = `Individual_Timesheets_${monthLabel}_${selectedYear}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            message.success('Individual files zipped successfully!');
        } catch (error) {
            message.error('Failed to generate ZIP.');
        } finally {
            setZipping(false);
        }
    };

    const renderTimesheet = (record, index) => {
        const days = Array.from({ length: 31 }, (_, i) => i + 1);
        const monthLabel = months.find(m => m.value === selectedMonth)?.label;

        return (
            <div key={index} className="timesheet-slip">
                <div className="slip-inner">
                    <div className="slip-header">
                    </div>

                    <div className="slip-info">
                        <div className="info-row">
                            <span className="label">Name:</span>
                            <span className="value bold">{record.name}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Company Name:</span>
                            <span className="value">{record.company}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Trade:</span>
                            <span className="value">{record.trade}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Month & Year:</span>
                            <span className="value bold">{monthLabel} {selectedYear}</span>
                        </div>
                    </div>

                    <table className="slip-table">
                        <thead>
                            <tr>
                                <th className="col-date">Date</th>
                                <th className="col-in">In</th>
                                <th className="col-out">Out</th>
                                <th className="col-hours">Total Hours</th>
                                <th className="col-sig">Signature</th>
                            </tr>
                        </thead>
                        <tbody>
                            {days.map((day) => (
                                <tr key={day}>
                                    <td>{day}</td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                </tr>
                            ))}
                            <tr className="total-row">
                                <td colSpan={3}>Grand Total</td>
                                <td></td>
                                <td className="no-right-border"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <style>
                {`
                @font-face {
                    font-family: 'Inter';
                    src: url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
                }

                @media print {
                    @page {
                        size: A4;
                        margin: 10mm;
                    }
                    body * {
                        visibility: hidden;
                    }
                    #print-section, #print-section * {
                        visibility: visible;
                    }
                    #print-section {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        background: white;
                    }
                    .no-print {
                        display: none !important;
                    }
                    .timesheet-slip {
                        width: 48%; /* 2 per row */
                        height: 270mm; /* Close to full page height */
                        display: inline-block;
                        vertical-align: top;
                        margin: 1%;
                        page-break-inside: avoid;
                        padding: 10px;
                        box-sizing: border-box;
                    }
                }

                /* Layout Styles */
                .timesheet-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
                    gap: 30px;
                    margin-top: 30px;
                }

                .timesheet-slip {
                    background: #fff;
                    border-radius: 4px;
                    padding: 20px;
                    font-family: 'Inter', -apple-system, sans-serif;
                    color: #333;
                }

                .slip-header {
                    height: 50px;
                }

                .slip-header h1 {
                    color: #003a8c;
                    border-bottom: 2px solid #003a8c;
                    display: inline-block;
                    padding-bottom: 2px;
                }

                .slip-info {
                    margin-bottom: 15px;
                }

                .info-row {
                    display: flex;
                    align-items: baseline;
                    margin-bottom: 5px;
                    border-bottom: 1px solid #eee;
                    padding-bottom: 2px;
                }

                .label {
                    font-size: 11px;
                    color: #666;
                    width: 110px;
                    flex-shrink: 0;
                    white-space: nowrap;
                }

                .value {
                    font-size: 13px;
                    color: #000;
                    flex: 1;
                }

                .bold {
                    font-weight: 700;
                }

                /* Table Styling */
                .slip-table {
                    width: 100%;
                    border-collapse: collapse;
                    border: 1px solid #000;
                }

                .slip-table th, .slip-table td {
                    border: 1px solid #000;
                    padding: 3px 5px;
                    text-align: center;
                    font-size: 10px;
                    height: 18px;
                }

                .slip-table th {
                    background: #f5f5f5;
                    font-weight: 700;
                    text-transform: uppercase;
                    font-size: 9px;
                }

                .col-date { width: 35px; }
                .col-in, .col-out { width: 50px; }
                .col-hours { width: 70px; }

                .total-row td {
                    font-weight: 700;
                    text-align: right;
                    background: #fafafa;
                }

                .no-right-border {
                    border-right: none !important;
                }

                .premium-card {
                    border-radius: 12px;
                    overflow: hidden;
                    border: none;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.06);
                }

                .gradient-header {
                    background: linear-gradient(135deg, #001529 0%, #003a8c 100%);
                    color: white;
                    padding: 24px;
                }
                `}
            </style>

            <Card className="no-print premium-card" style={{ padding: 0 }}>
                <div className="gradient-header">
                    <Title level={3} style={{ color: 'white', margin: 0 }}>
                        <PrinterOutlined style={{ marginRight: 12 }} />
                        Blank Timesheet Generator
                    </Title>
                    <Paragraph style={{ color: 'rgba(255,255,255,0.85)', marginTop: 8, marginBottom: 0 }}>
                        Manage and print blank timesheets based on monthly deployment lists.
                    </Paragraph>
                </div>
                
                <div style={{ padding: '24px' }}>
                    <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', alignItems: 'flex-end', background: '#f9f9f9', padding: '15px', borderRadius: '8px' }}>
                        <Space direction="vertical">
                            <Text strong>Select Month</Text>
                            <Select 
                                value={selectedMonth} 
                                options={months} 
                                style={{ width: 160 }} 
                                onChange={setSelectedMonth}
                            />
                        </Space>
                        <Space direction="vertical">
                            <Text strong>Select Year</Text>
                            <Select 
                                value={selectedYear} 
                                options={years} 
                                style={{ width: 120 }} 
                                onChange={setSelectedYear}
                            />
                        </Space>
                        <Button icon={<SearchOutlined />} onClick={fetchData}>Refresh List</Button>
                    </div>

                    <Alert
                        message="Upload Deployment List"
                        description={
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                <li>The Excel should have columns: <strong>Name, Trade, Company</strong></li>
                                <li>Uploading for the same Month/Year will <strong>replace</strong> the existing list.</li>
                            </ul>
                        }
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        style={{ marginBottom: 24 }}
                    />

                    <Space size="large" wrap>
                        <Upload
                            beforeUpload={(file) => {
                                setFile(file);
                                return false; 
                            }}
                            maxCount={1}
                            accept=".xlsx,.xls,.csv"
                            showUploadList={!!file}
                        >
                            <Button size="large" icon={<FileExcelOutlined />} style={{ borderRadius: '8px' }}>
                                Select Excel File
                            </Button>
                        </Upload>

                        <Button 
                            type="primary" 
                            size="large" 
                            onClick={handleUpload} 
                            loading={loading}
                            disabled={!file}
                            style={{ borderRadius: '8px', minWidth: '140px' }}
                        >
                            Process & Save
                        </Button>

                        <Divider type="vertical" style={{ height: '40px' }} />

                        {data.length > 0 && (
                            <Space>
                                <Button 
                                    type="primary" 
                                    size="large" 
                                    icon={<PrinterOutlined />} 
                                    onClick={handlePrint}
                                    style={{ borderRadius: '8px', background: '#52c41a', borderColor: '#52c41a' }}
                                >
                                    Print {data.length} Slips
                                </Button>
                                <Button 
                                    type="default" 
                                    size="large" 
                                    icon={<DownloadOutlined />} 
                                    onClick={handleBulkDownload}
                                    loading={downloading}
                                    style={{ borderRadius: '8px' }}
                                >
                                    Download PDF (All)
                                </Button>
                                <Button 
                                    type="default" 
                                    size="large" 
                                    icon={<FileZipOutlined />} 
                                    onClick={handleZipDownload}
                                    loading={zipping}
                                    style={{ borderRadius: '8px' }}
                                >
                                    Download ZIP (Individual)
                                </Button>
                            </Space>
                        )}
                    </Space>
                </div>
            </Card>

            {loading ? (
                <div style={{ textAlign: 'center', marginTop: 100 }} className="no-print">
                    <Spin size="large" tip="Loading data..." />
                </div>
            ) : data.length > 0 ? (
                <div id="print-section">
                    <div className="timesheet-grid">
                        {data.map((record, index) => renderTimesheet(record, index))}
                    </div>
                </div>
            ) : (
                <div className="no-print" style={{ marginTop: 60 }}>
                    <Empty 
                        image={Empty.PRESENTED_IMAGE_SIMPLE} 
                        description={
                            <Text type="secondary" style={{ fontSize: '16px' }}>
                                No deployment data found for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}. 
                                <br />Please upload an Excel file to get started.
                            </Text>
                        } 
                    />
                </div>
            )}
        </div>
    );
};

export default BlankTimesheets;


